import clsx from "clsx";
import React, { useState, Fragment } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import { red, blue, grey } from "@material-ui/core/colors";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../reducers";
import { Torrent, TorrentStatus } from "../../types";
import { setActiveTorrent, setSelectedTorrents, updateSearchStatus } from "../../actions";
import { size, datetime, percent, round } from "../util";
import Speed from "../Shared/Speed";
import { Chip, Typography, Button } from "@material-ui/core";
import { thunkFetchTorrents } from "../../thunks";

function desc(a: any, b: any, orderBy: string) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array: Array<any>, cmp: (a: any, b: any) => number) {
  const stabilizedThis = array.map((el: any, index: number) => [el, index]);
  stabilizedThis.sort((a: any, b: any) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el: any) => el[0]);
}

function getSorting(order: string, orderBy: string) {
  return order === "desc"
    ? (a: any, b: any) => desc(a, b, orderBy)
    : (a: any, b: any) => -desc(a, b, orderBy);
}

function EnhancedTableHead(props: any) {
  const {
    classes,
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort
  } = props;
  const createSortHandler = (property: any) => (event: any) => {
    onRequestSort(event, property);
  };
  const headCells = [
    { id: "id", align: "right", disablePadding: false, label: "ID" },
    { id: "name", align: "left", disablePadding: false, label: "Name" },
    { id: "size", align: "center", disablePadding: false, label: "Size" },
    { id: "status", align: "center", disablePadding: false, label: "Status" },
    {
      id: "createdAt",
      align: "center",
      disablePadding: false,
      label: "Created At"
    },
    {
      id: "progress",
      align: "center",
      disablePadding: false,
      label: "Progress"
    },
    { id: "ratio", align: "center", disablePadding: false, label: "Ratio" },
    {
      id: "activityDate",
      align: "center",
      disablePadding: false,
      label: "Last Activity"
    }
  ];

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={numSelected === rowCount}
            onChange={onSelectAllClick}
            inputProps={{ "aria-label": "select all torrents" }}
          />
        </TableCell>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={headCell.align as any}
            padding={headCell.disablePadding ? "none" : "default"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={order}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span className={classes.visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  classes: PropTypes.object.isRequired,
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired
};

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    flexGrow: 1
  },
  table: {
    minWidth: 750
  },
  tableWrapper: {
    overflowX: "auto"
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1
  },
  placeholder: {
    height: "100%",
    textAlign: "center",
    display: "flex",
    flexDirection: "column"
  },
  error: {
    color: red[400]
  },
  select: {
    background: grey[200]
  },
  active: {
    background: blue[200]
  },
  loadHint: {
    textAlign: "center",
    margin: "5px",
    fontSize: "12px"
  }
}));

export default function List() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("id");
  // const [selected, setSelected] = useState<number[]>([]);
  const torrents = useSelector((state: RootState) => state.torrent.list);
  const loading = useSelector((state: RootState) => state.torrent.pending);
  const count = useSelector((state: RootState) => state.torrent.count);
  const error = useSelector((state: RootState) => state.torrent.error);
  const activeTorrent = useSelector((state: RootState) => state.torrent.active);
  const selected = useSelector((state: RootState) => state.torrent.selected);
  const setSelected = (ids: number[]) => dispatch(setSelectedTorrents(ids));

  const handleRequestSort = (event: any, property: any) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && torrents) {
      setSelected(torrents.map(t => t.id as number));
      return;
    }
    setSelected([]);
  };

  const handleCheckboxClick = (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleRowClick = (torrent: Torrent) => {
    dispatch(setActiveTorrent(torrent));
  };

  const handleLoadMore = () => {
    dispatch(thunkFetchTorrents('loadMore'));
  };

  const handleLoadAll = () => {
    dispatch(thunkFetchTorrents('loadAll'));
  };

  const handleArchiveMode = () => {
    dispatch(updateSearchStatus(TorrentStatus.PUSHED));
    setOrder('asc');
    setOrderBy('activityDate')
    dispatch(thunkFetchTorrents('loadAll'));
  }

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  return (
    <div className={classes.root}>
      {torrents && torrents.length > 0 && (
        <div className={classes.tableWrapper}>
          <Typography variant="subtitle1" className={classes.loadHint}>
            Found <strong>{count}</strong>, <strong>{torrents.length}</strong>{" "}
            loaded.
            {torrents.length < count && (
              <Fragment>
                {/* SCROLL TO LOAD MORE , or */}
                <Button size="small" onClick={handleLoadMore}>
                  LOAD MORE
                </Button>
                <Button size="small" onClick={handleLoadAll}>
                  LOAD ALL
                </Button>
              </Fragment>
            )}
            <Button size="small" onClick={handleArchiveMode}>
              ARCHIVE MODE
            </Button>
          </Typography>
          <div></div>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size="medium"
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={torrents.length}
            />
            <TableBody>
              {stableSort(torrents, getSorting(order, orderBy)).map(
                (row: Torrent, index: number) => {
                  const isItemSelected = isSelected(row.id);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  let statusText = row.status as string;
                  if (row.state) {
                    statusText += `:${row.state}`
                  }
                  const rowColor =
                    row.status === "ERROR"
                      ? "secondary"
                      : row.status === TorrentStatus.PUSHED
                        ? "primary"
                        : "default";

                  return (
                    <TableRow
                      hover
                      onClick={e => handleRowClick(row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.id}
                      className={clsx(
                        isItemSelected && classes.select,
                        activeTorrent &&
                        row.id === activeTorrent.id &&
                        classes.active
                      )}
                    >
                      <TableCell
                        padding="checkbox"
                        onClick={e => handleCheckboxClick(e, row.id)}
                      >
                        <Checkbox
                          checked={isItemSelected}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        component="th"
                        id={labelId}
                        scope="row"
                      >
                        {row.id}
                      </TableCell>
                      <TableCell align="left">
                        {row.name}{" "}
                        <Speed
                          uploadSpeed={row.uploadSpeed}
                          downloadSpeed={row.downloadSpeed}
                        />
                      </TableCell>
                      <TableCell align="center">{size(row.size)}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusText}
                          size="small"
                          color={rowColor}
                        />
                        {row.pending && <CircularProgress size={15} />}
                      </TableCell>
                      <TableCell align="center">
                        {datetime(row.createdAt)}
                      </TableCell>
                      <TableCell align="center">
                        {percent(row.progress)}
                      </TableCell>
                      <TableCell align="center">{round(row.uploadRatio, 1)}</TableCell>
                      <TableCell align="center">
                        {datetime(row.activityDate)}
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>
        </div>
      )}
      {loading && (
        <div className="placeholder">
          <CircularProgress />
        </div>
      )}
      {!loading && (!torrents || torrents.length === 0) && (
        <div className="placeholder">
          {error && (
            <span className={classes.error}>
              {error.name}: {error.message}
            </span>
          )}
          {!error && <span>no data</span>}
        </div>
      )}
    </div>
  );
}
